/**
 * Vector (SVG) drawing context for the PDF export (WI #5531).
 *
 * Wraps an `svgcanvas` Context so the existing scene painters can draw into it
 * unchanged — the same `(ctx, scene)` painters that drive the on-screen canvas
 * produce the export, so there is exactly ONE renderer to maintain. Four small
 * adaptations bridge the gaps between a real `CanvasRenderingContext2D` and the
 * shim:
 *
 *   1. `fill()` / `stroke()` / `clip()` with a `Path2D` — the shim's are no-arg.
 *      We re-trace the original SVG path-data (stashed on the `Path2D` by
 *      `draw/shapes.ts`) and then fill/stroke/clip the live path. Activity
 *      hexagons + phase ribbons; `clip` is the group marker's segmented face,
 *      which without this throws inside the shim and aborts its painter.
 *   2. Shadows are dropped — export is the flat rendered state, no glows. The
 *      `shadow*` setters become no-ops (also sidesteps the shim's filter quirks).
 *   3. `createPattern` returns `null` — the painters already fall back to a flat
 *      translucent fill when no pattern is available (`draw/shapes.ts`).
 *   4. `save()`/`restore()` are depth-counted so `resetState()` can unwind a
 *      painter that died mid-`save()` — see the `ExportContext` interface.
 */
import { Context } from 'svgcanvas';
import { SVG_PATH_DATA } from '../draw/shapes';
import { pathDataOf, tracePathData } from './svgPath';

export interface ExportContext {
  /** The painters draw against this, typed as a normal 2D context. */
  readonly ctx: CanvasRenderingContext2D;
  /**
   * Unwind every `save()` still open and reset the transform to the identity.
   * A painter that throws between `save()` and `restore()` otherwise leaves its
   * `translate` on the shared context, and every later painter — the workstream
   * sidebar included — is drawn at that offset.
   */
  resetState(): void;
  /** Serialise everything painted so far to an SVG document string. */
  toSvg(): string;
}

/** The shim is API-compatible with a 2D context, plus its SVG serialiser. */
type SvgCanvasContext = CanvasRenderingContext2D & {
  getSerializedSvg(fixNamedEntities?: boolean): string;
};

/** Create a vector export context sized to the full map extent (CSS pixels). */
export function createExportContext(
  width: number,
  height: number,
): ExportContext {
  const svg = new Context(
    Math.ceil(width),
    Math.ceil(height),
  ) as unknown as SvgCanvasContext;

  // Re-trace a tagged Path2D, then invoke the shim's no-arg fill/stroke/clip.
  const rawFill = svg.fill.bind(svg) as (fillRule?: CanvasFillRule) => void;
  const rawStroke = svg.stroke.bind(svg) as () => void;
  const rawClip = svg.clip.bind(svg) as () => void;
  const rawSave = svg.save.bind(svg) as () => void;
  const rawRestore = svg.restore.bind(svg) as () => void;
  const target = svg as unknown as CanvasRenderingContext2D;
  let depth = 0;

  svg.fill = ((arg?: Path2D | CanvasFillRule, rule?: CanvasFillRule): void => {
    if (arg instanceof Path2D) {
      const d = pathDataOf(arg, SVG_PATH_DATA);
      // Untagged Path2D: we can't replay it, so skip — calling the shim's no-arg
      // fill here would re-apply the stale current path and corrupt a neighbour.
      if (!d) {
        return;
      }
      tracePathData(target, d);
      rawFill(rule);

      return;
    }
    rawFill(arg as CanvasFillRule | undefined);
  }) as CanvasRenderingContext2D['fill'];

  svg.stroke = ((arg?: Path2D): void => {
    if (arg instanceof Path2D) {
      const d = pathDataOf(arg, SVG_PATH_DATA);
      if (!d) {
        return;
      }
      tracePathData(target, d);
    }
    rawStroke();
  }) as CanvasRenderingContext2D['stroke'];

  svg.clip = ((arg?: Path2D | CanvasFillRule): void => {
    if (arg instanceof Path2D) {
      const d = pathDataOf(arg, SVG_PATH_DATA);
      // Untagged: clip nothing rather than clip by the stale current element —
      // the shim would try to re-parent it and throw.
      if (!d) {
        return;
      }
      tracePathData(target, d);
    }
    rawClip();
  }) as CanvasRenderingContext2D['clip'];

  svg.save = (): void => {
    depth++;
    rawSave();
  };

  // An unmatched restore would pop the shim's group stack past its root.
  svg.restore = (): void => {
    if (depth === 0) {
      return;
    }
    depth--;
    rawRestore();
  };

  // Patterns → flat-fill fallback (painters handle null); shadows → dropped.
  svg.createPattern = (() => null) as CanvasRenderingContext2D['createPattern'];
  silenceShadows(svg);

  return {
    ctx: target,
    resetState: (): void => {
      while (depth > 0) {
        svg.restore();
      }
      target.setTransform(1, 0, 0, 1, 0, 0);
      target.globalAlpha = 1;
    },
    toSvg: () => svg.getSerializedSvg(true),
  };
}

/** Make the shadow* properties inert so painters' glows don't emit SVG filters. */
function silenceShadows(svg: object): void {
  for (const prop of [
    'shadowBlur',
    'shadowColor',
    'shadowOffsetX',
    'shadowOffsetY',
  ]) {
    Object.defineProperty(svg, prop, {
      get: () => (prop === 'shadowColor' ? 'rgba(0,0,0,0)' : 0),
      set: () => undefined,
      configurable: true,
    });
  }
}
